import {
    all,
    call,
    put,
    select,
    takeLatest,
} from 'redux-saga/effects';

import checkPermission from '../../@paco/helpers/permissions/checkPermission';
import { getCheckedFormOptionsValue } from '../../@paco/helpers/types/formOption';
import { getStartDate } from '../../components/WeekNavigator/Controls/Controls';
import {
    LEAVE_OF_ABSENCE_REQUESTS,
    LEAVE_OF_ABSENCE_REQUESTS_CALENDAR,
    LEAVE_OF_ABSENCES_REQUEST_APPROVED,
    LEAVE_OF_ABSENCES_REQUEST_OPEN,
    TOAST_TYPE_FAIL,
    TOAST_TYPE_PASS,
} from '../../constants';
import { generateDownloadURI, getObjProperty } from '../../helpers';
import {
    getAbsences,
    getAbsencesInDateRange,
    getAbsencesInDateRangeCSV,
    getLeaveOfAbsences,
    postLeaveOfAbsencesToLoket,
} from '../../helpers/api/absencesApi';
import { delUserFromShift } from '../../helpers/api/shiftsApi';
import { postShiftPlanning } from '../../helpers/api-ts/shiftPlanning';
import { formatDate } from '../../helpers/date';
import { translate } from '../../helpers/translations/translator';
import { deductOneSecondFromLOARequests } from '../absences-ts/absencesHelpers';
import { getAndApplyLeaveOfAbsenceLoketLeaveBalances } from '../absences-ts/absencesSaga';
import {
    ABSENCES_EXPORT_CSV_FAILURE,
    ABSENCES_EXPORT_CSV_REQUEST,
    ABSENCES_EXPORT_CSV_SUCCESS,
    ABSENCES_GET_ABSENCES_FAILURE,
    ABSENCES_GET_ABSENCES_IN_DATE_RANGE_FAILURE,
    ABSENCES_GET_ABSENCES_IN_DATE_RANGE_REQUEST,
    ABSENCES_GET_ABSENCES_IN_DATE_RANGE_SUCCESS,
    ABSENCES_GET_ABSENCES_REQUEST,
    ABSENCES_GET_ABSENCES_SUCCESS,
    ABSENCES_GET_CALENDAR_LEAVE_OF_ABSENCE_REQUESTS_SUCCESS,
    ABSENCES_GET_DATA_FOR_SHIFTS_AND_USERS_FAILURE,
    ABSENCES_GET_DATA_FOR_SHIFTS_AND_USERS_REQUEST,
    ABSENCES_GET_DATA_FOR_SHIFTS_AND_USERS_SUCCESS,
    ABSENCES_GET_LEAVE_OF_ABSENCE_REQUESTS_FAILURE,
    ABSENCES_GET_LEAVE_OF_ABSENCE_REQUESTS_REQUEST,
    ABSENCES_GET_OPEN_LEAVE_OF_ABSENCE_REQUESTS_SUCCESS,
    ABSENCES_REPLACE_USER_IN_SHIFT_FAILURE,
    ABSENCES_REPLACE_USER_IN_SHIFT_REQUEST,
    ABSENCES_REPLACE_USER_IN_SHIFT_SUCCESS,
    ABSENCES_SAGA_EXPORT_CSV,
    ABSENCES_SAGA_GET_ABSENCES,
    ABSENCES_SAGA_GET_ABSENCES_IN_DATE_RANGE,
    ABSENCES_SAGA_GET_LEAVE_OF_ABSENCE_REQUESTS,
    ABSENCES_SAGA_REPLACE_USER_IN_SHIFT,
    ABSENCES_SAGA_SYNC_LEAVE_OF_ABSENCES_TO_LOKET,
    ABSENCES_SYNC_LEAVE_OF_ABSENCES_TO_LOKET_FAILURE,
    ABSENCES_SYNC_LEAVE_OF_ABSENCES_TO_LOKET_REQUEST,
    ABSENCES_SYNC_LEAVE_OF_ABSENCES_TO_LOKET_SUCCESS,
    APP_SAGA_ADD_TOAST,
} from '../actionTypes';
import { getCurrentUserFromStore } from '../app/appHelpers';
import { setPagination } from '../app/appSaga';

export function* fetchLeaveOfAbsenceRequests(action) {
    yield put({ ...action, type: ABSENCES_GET_LEAVE_OF_ABSENCE_REQUESTS_REQUEST });

    try {
        const state = yield select();
        const { startDate, endDate } = state.weekNavigatorReducer;
        const { leaveOfAbsencesStatus: status, leaveOfAbsencesPage: page } = state.absencesReducer;
        const { filter } = state.filterReducer;
        const { departmentOptions } = state.globalFiltersReducer;
        const departments = getCheckedFormOptionsValue(departmentOptions);
        const includePendingChangesets = page === LEAVE_OF_ABSENCE_REQUESTS;

        const response = yield call(() => getLeaveOfAbsences(
            startDate,
            endDate,
            status,
            departments,
            filter,
            page,
            state.paginationReducer,
            includePendingChangesets,
        ));

        yield call(() => setPagination(response.meta));
        const leaveOfAbsencesRequests = deductOneSecondFromLOARequests(response.data);

        yield put({
            type: page !== LEAVE_OF_ABSENCE_REQUESTS_CALENDAR
                ? ABSENCES_GET_OPEN_LEAVE_OF_ABSENCE_REQUESTS_SUCCESS
                : ABSENCES_GET_CALENDAR_LEAVE_OF_ABSENCE_REQUESTS_SUCCESS,
            leaveOfAbsencesRequests,
        });

        yield getAndApplyLeaveOfAbsenceLoketLeaveBalances();
    } catch (error) {
        console.error(error);
        yield put({ type: ABSENCES_GET_LEAVE_OF_ABSENCE_REQUESTS_FAILURE });
    }
}

export function* fetchAbsencesDataForShiftsAndUsers() {
    const state = yield select();
    const { startDate, endDate } = state.weekNavigatorReducer;
    const currentUser = yield getCurrentUserFromStore();
    const { permissions } = currentUser;
    yield put({ type: ABSENCES_GET_DATA_FOR_SHIFTS_AND_USERS_REQUEST, startDate, endDate });

    try {
        const status = [LEAVE_OF_ABSENCES_REQUEST_OPEN, LEAVE_OF_ABSENCES_REQUEST_APPROVED];
        const canViewAllAbsences = checkPermission(permissions, 'view-all-absences');

        const responses = yield all([
            getLeaveOfAbsences(startDate, endDate, status),
            canViewAllAbsences ? getAbsencesInDateRange(startDate, endDate) : { data: [] },
        ]);

        const leaveOfAbsencesRequests = responses[0];
        const absences = responses[1];

        yield put({
            type: ABSENCES_GET_DATA_FOR_SHIFTS_AND_USERS_SUCCESS,
            leaveOfAbsencesRequests: leaveOfAbsencesRequests.data,
            absences: absences.data,
        });
    } catch (error) {
        console.error(error);
        yield put({ type: ABSENCES_GET_DATA_FOR_SHIFTS_AND_USERS_FAILURE });
    }
}

function* fetchAbsences() {
    yield put({ type: ABSENCES_GET_ABSENCES_REQUEST });

    try {
        const state = yield select();
        const { filter } = state.filterReducer;
        const { departmentOptions } = state.globalFiltersReducer;
        const departmentIds = getCheckedFormOptionsValue(departmentOptions);

        const response = yield call(() => getAbsences(filter, departmentIds, state.paginationReducer));

        yield call(() => setPagination(response.meta));

        yield put({ type: ABSENCES_GET_ABSENCES_SUCCESS, absences: response.data });
    } catch (error) {
        yield put({ type: ABSENCES_GET_ABSENCES_FAILURE });
    }
}

export function* fetchAbsencesInDateRange() {
    yield put({ type: ABSENCES_GET_ABSENCES_IN_DATE_RANGE_REQUEST });

    try {
        const state = yield select();
        const { startDate, endDate } = state.weekNavigatorReducer;
        const { filter } = state.filterReducer;
        const { departmentOptions } = state.globalFiltersReducer;
        const departments = getCheckedFormOptionsValue(departmentOptions);

        const response = yield call(() => getAbsencesInDateRange(startDate, endDate, filter, departments, state.paginationReducer));

        yield call(() => setPagination(response.meta));

        yield put({ type: ABSENCES_GET_ABSENCES_IN_DATE_RANGE_SUCCESS, absences: response.data });
    } catch (error) {
        yield put({ type: ABSENCES_GET_ABSENCES_IN_DATE_RANGE_FAILURE });
    }
}

function* replaceUserInShift(action) {
    yield put({ type: ABSENCES_REPLACE_USER_IN_SHIFT_REQUEST });
    try {
        if (action.addUserId) {
            yield call(() => postShiftPlanning(
                { userId: action.addUserId, shiftId: action.shiftId },
            ));
        }
        yield call(() => delUserFromShift(action.shiftId, action.delUserId));
        yield put({ type: ABSENCES_SAGA_GET_ABSENCES });
        yield put({ type: ABSENCES_REPLACE_USER_IN_SHIFT_SUCCESS });
        yield put({ type: APP_SAGA_ADD_TOAST, toast: translate('pages.absences.replaceUserSuccess'), toastType: TOAST_TYPE_PASS });
    } catch (error) {
        yield put({ type: ABSENCES_REPLACE_USER_IN_SHIFT_FAILURE });
    }
}

function* exportCSV() {
    yield put({ type: ABSENCES_EXPORT_CSV_REQUEST });
    try {
        const state = yield select();
        const { startDate, endDate, mode } = state.weekNavigatorReducer;
        const start = getStartDate(startDate, mode);
        const { filter } = state.filterReducer;
        const { departmentOptions } = state.globalFiltersReducer;
        const departmentIds = getCheckedFormOptionsValue(departmentOptions);

        const response = yield call(() => getAbsencesInDateRangeCSV(start, endDate, departmentIds, filter));
        const filename = `${translate('pages.absences.absences').toLowerCase()}-${formatDate(start, 'dd-MM-yyyy')}-${formatDate(endDate, 'dd-MM-yyyy')}.csv`;
        generateDownloadURI(`data:text/html,${response.data}`, filename);
        yield put({ type: ABSENCES_EXPORT_CSV_SUCCESS });
    } catch (error) {
        yield put({ type: ABSENCES_EXPORT_CSV_FAILURE });
    }
}

function* syncLeaveOfAbsencesToLoket(action) {
    yield put({ type: ABSENCES_SYNC_LEAVE_OF_ABSENCES_TO_LOKET_REQUEST });
    try {
        yield call(() => postLeaveOfAbsencesToLoket(action.startDate, action.endDate));
        yield put({ type: ABSENCES_SYNC_LEAVE_OF_ABSENCES_TO_LOKET_SUCCESS });
        yield put({ type: APP_SAGA_ADD_TOAST, toast: translate('common.syncLoketSuccess', { type: 'Verloven' }), toastType: TOAST_TYPE_PASS });
    } catch (error) {
        yield put({ type: ABSENCES_SYNC_LEAVE_OF_ABSENCES_TO_LOKET_FAILURE });
        yield put({ type: APP_SAGA_ADD_TOAST, toast: getObjProperty(error, 'meta.message'), toastType: TOAST_TYPE_FAIL });
    }
}

export default function* absencesWatcher() {
    yield takeLatest(ABSENCES_SAGA_GET_LEAVE_OF_ABSENCE_REQUESTS, fetchLeaveOfAbsenceRequests);
    yield takeLatest(ABSENCES_SAGA_GET_ABSENCES, fetchAbsences);
    yield takeLatest(ABSENCES_SAGA_GET_ABSENCES_IN_DATE_RANGE, fetchAbsencesInDateRange);
    yield takeLatest(ABSENCES_SAGA_REPLACE_USER_IN_SHIFT, replaceUserInShift);
    yield takeLatest(ABSENCES_SAGA_EXPORT_CSV, exportCSV);
    yield takeLatest(ABSENCES_SAGA_SYNC_LEAVE_OF_ABSENCES_TO_LOKET, syncLeaveOfAbsencesToLoket);
}
